import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import AssessmentApproval from "./AssessmentApproval";
import ReviewApplication from "./ReviewApplication";
import { getFirestore, collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import app from "../../firebase";
import ResetPassword from "../admin/users/ResetPassword";
import { toCalendarDateKey } from "../../utils/announcementDates";
// Lightweight SVG charts (inline) to avoid external chart library compatibility issues
const LineSVG = ({ width = 700, height = 280, data = [] }) => {
  if (!data || data.length === 0) return <div style={{ height }} />;
  const padding = 24;
  const w = width - padding * 2;
  const h = height - padding * 2;
  const max = Math.max(...data.map(d => d.applicants || 0), 1);
  const stepX = w / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = padding + i * stepX;
    const y = padding + (1 - (d.applicants || 0) / max) * h;
    return { x, y, label: d.month, value: d.applicants || 0 };
  });
  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
  return (
    <svg width={width} height={height} role="img" aria-label="Applicant trend">
      <rect x={0} y={0} width={width} height={height} rx={8} fill="transparent" />
      <g>
        {/* grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => (
          <line key={idx} x1={padding} x2={width - padding} y1={padding + t * h} y2={padding + t * h} stroke="#f0f0f0" strokeWidth={1} />
        ))}
        {/* area */}
        <path d={`${pathD} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`} fill="rgba(21,92,43,0.08)" stroke="none" />
        {/* line */}
        <path d={pathD} fill="none" stroke="#155c2b" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {/* points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3.5} fill="#155c2b" />
          </g>
        ))}
      </g>
      {/* labels */}
      <g>
        {points.map((p, i) => (
          <text key={i} x={p.x} y={height - 6} fontSize={11} fill="#667085" textAnchor="middle">{p.label}</text>
        ))}
      </g>
    </svg>
  );
};

const PieSVG = ({ width = 380, height = 280, data = [], colors = ['#e67e22', '#1e8f4a', '#155c2b'] }) => {
  const cx = width / 2;
  const cy = height / 2 - 10;
  const radius = Math.min(width, height) / 2 - 20;
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  let startAngle = -90;
  const arcs = data.map((d) => {
    const value = d.value || 0;
    const angle = (value / total) * 360;
    const endAngle = startAngle + angle;
    const large = angle > 180 ? 1 : 0;
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const dPath = [`M ${cx} ${cy}`, `L ${start.x} ${start.y}`, `A ${radius} ${radius} 0 ${large} 0 ${end.x} ${end.y}`, 'Z'].join(' ');
    startAngle = endAngle;
    return { dPath, label: d.name, value: value };
  });
  return (
    <svg width={width} height={height} role="img" aria-label="Application status share">
      <g>
        {arcs.map((a, i) => (
          <path key={i} d={a.dPath} fill={colors[i % colors.length]} stroke="#fff" strokeWidth={1} />
        ))}
      </g>
      <g>
        {/* legend */}
        {data.map((d, i) => (
          <g key={i} transform={`translate(${20}, ${height - 60 + i * 18})`}>
            <rect width={12} height={12} fill={colors[i % colors.length]} />
            <text x={18} y={10} fontSize={12} fill="#333">{d.name} ({d.value})</text>
          </g>
        ))}
      </g>
    </svg>
  );
};

function polarToCartesian(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };

}

const SIDEBAR_BUTTONS = [
  { key: "home", label: "Home" },
  { key: "assessment-approval", label: "Entrance Exam Approval" },
  { key: "review-application", label: "Review Application" },
];

export default function AdmissionDashboard() {
  const [active, setActive] = useState("home");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { logout, currentUser } = useSession();
  const [counselorEmail, setCounselorEmail] = useState(currentUser?.email || "");
  const [showResetModal, setShowResetModal] = useState(false);
  // Dummy profile info for header (replace with real user info if available)
  const profilePicUrl = "https://ui-avatars.com/api/?name=Admission";

  // Analytics state
  const [announcement, setAnnouncement] = useState(null);
  const [students, setStudents] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("all");
  const [selectedSemester, setSelectedSemester] = useState("all");

  // deduplicated school years (show one entry per school year label)
  const uniqueSchoolYears = useMemo(() => {
    const map = new Map();
    schoolYears.forEach(s => {
      if (!map.has(s.label)) map.set(s.label, s);
    });
    return Array.from(map.values());
  }, [schoolYears]);

  // Load announcement settings (school year / semester dates)
  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    let mounted = true;
    getDoc(ref).then(snap => {
      if (!mounted) return;
      if (snap.exists()) {
        const data = snap.data();
        setAnnouncement(data);
        const years = [];
        if (data.firstSemYearStart && data.firstSemYearEnd) {
          years.push({ key: `first-${data.firstSemYearStart}-${data.firstSemYearEnd}`, label: `${data.firstSemYearStart}-${data.firstSemYearEnd}`, sem: "first", start: data.firstSemStart, end: data.firstSemEnd });
        }
        if (data.secondSemYearStart && data.secondSemYearEnd) {
          years.push({ key: `second-${data.secondSemYearStart}-${data.secondSemYearEnd}`, label: `${data.secondSemYearStart}-${data.secondSemYearEnd}`, sem: "second", start: data.secondSemStart, end: data.secondSemEnd });
        }
        setSchoolYears(years);
      }
    }).catch(e => console.error("Failed to load announcement settings", e));
    return () => { mounted = false; };
  }, []);

  // Listen to students collection for analytics
  useEffect(() => {
    const db = getFirestore(app);
    const col = collection(db, "students");
    const unsub = onSnapshot(col, snap => {
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setStudents(arr);
    }, err => {
      console.error("students snapshot error", err);
      setStudents([]);
    });
    return unsub;
  }, []);

  // Helper: parse various createdAt formats to Date
  const parseStudentDate = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return new Date(val);
    if (typeof val?.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    return null;
  };

  // Determine date range based on selected school year and semester
  const selectedRange = useMemo(() => {
    if (selectedSchoolYear === 'all' || !announcement) return null;
    const fy1 = announcement.firstSemYearStart && announcement.firstSemYearEnd ? `${announcement.firstSemYearStart}-${announcement.firstSemYearEnd}` : null;
    const fy2 = announcement.secondSemYearStart && announcement.secondSemYearEnd ? `${announcement.secondSemYearStart}-${announcement.secondSemYearEnd}` : null;
    if (selectedSemester === 'first') {
      if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
      if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
      return null;
    }
    if (selectedSemester === 'second') {
      if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
      if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
      return null;
    }
    if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
    if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
    return null;
  }, [selectedSchoolYear, selectedSemester, schoolYears]);

  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      if (!selectedRange) return true;
      const d = parseStudentDate(s.createdAt);
      if (!d) return false;
      const studentDateKey = toCalendarDateKey(d);
      const startKey = toCalendarDateKey(selectedRange.start);
      const endKey = toCalendarDateKey(selectedRange.end);
      return (!startKey || studentDateKey >= startKey) && (!endKey || studentDateKey <= endKey);
    });
  }, [students, selectedRange]);

  const metrics = useMemo(() => {
    const applicantsReview = filteredStudents.filter(s => !s.idNumber).length;
    const applicantsApproved = filteredStudents.filter(s => s.idNumber).length;
    const totalApplicants = applicantsReview + applicantsApproved;

    const categoryCounts = {};
    const programCounts = {};
    filteredStudents.forEach(s => {
      const cat = s.appliedCategory || s.initialCategory || s.category || 'Unknown';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
      const p = s.program || 'Unspecified';
      programCounts[p] = (programCounts[p] || 0) + 1;
    });

    const enrolledCount = filteredStudents.filter(s => s.registrationStatus === 'enrolled').length;
    const passCount = filteredStudents.filter(s => s.appointment?.status === 'approved').length;
    const failCount = filteredStudents.filter(s => s.appointment?.status === 'disapproved').length;

    return { applicantsReview, applicantsApproved, totalApplicants, categoryCounts, enrolledCount, programCounts, passCount, failCount };
  }, [filteredStudents]);

  const trendLabels = useMemo(() => {
    const labels = [];
    if (selectedRange) {
      const startKey = toCalendarDateKey(selectedRange.start);
      const endKey = toCalendarDateKey(selectedRange.end);
      if (startKey && endKey && startKey <= endKey) {
        const [startYear, startMonth] = startKey.split("-").map(Number);
        const [endYear, endMonth] = endKey.split("-").map(Number);
        const current = new Date(Date.UTC(startYear, startMonth - 1, 1));
        const last = new Date(Date.UTC(endYear, endMonth - 1, 1));
        while (current <= last) {
          labels.push(`${current.toLocaleString('default', { month: 'short', timeZone: "UTC" })} ${current.getUTCFullYear()}`);
          current.setUTCMonth(current.getUTCMonth() + 1);
        }
      }
    }
    if (labels.length === 0) {
      const now = new Date();
      const current = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      for (let i = 0; i < 6; i += 1) {
        labels.push(`${current.toLocaleString('default', { month: 'short' })} ${current.getFullYear()}`);
        current.setMonth(current.getMonth() + 1);
      }
    }
    return labels;
  }, [selectedRange]);

  const trendData = useMemo(() => {
    const counts = trendLabels.map(() => 0);
    filteredStudents.forEach(s => {
      const d = parseStudentDate(s.createdAt);
      if (!d) return;
      const label = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      const index = trendLabels.indexOf(label);
      if (index >= 0) counts[index] += 1;
    });
    return trendLabels.map((label, index) => ({ month: label, applicants: counts[index] }));
  }, [filteredStudents, trendLabels]);

  const statusData = useMemo(() => ([
    { name: 'Review', value: metrics.applicantsReview },
    { name: 'Approved', value: metrics.applicantsApproved },
    { name: 'Enrolled', value: metrics.enrolledCount },
  ]), [metrics]);

  // Return to the Admission login when the session ends in another tab.
  useEffect(() => {
    if (currentUser === null) {
      navigate("/admission-login");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    try { setCounselorEmail(currentUser?.email || ""); } catch (e) {}
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('admissionPortalLogin');
      localStorage.removeItem('admissionPortalRole');
      navigate("/admission-login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      {/* Sidebar (left) */}
      <div style={{ width: 240, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", position: 'fixed', top: 0, left: 0, height: '100vh', boxShadow: "2px 0 8px #eee", overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 24, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        {SIDEBAR_BUTTONS.map(btn => (
          <button
            key={btn.key}
            onClick={() => setActive(btn.key)}
            style={{
              background: active === btn.key ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 32px",
              fontWeight: 600,
              fontSize: 16,
              cursor: "pointer",
              textAlign: "left",
              marginBottom: 8,
              marginRight: 0,
              transition: "background 0.2s, color 0.2s",
              boxShadow: active === btn.key ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            {btn.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
      </div>

      {/* Main area: header at top, content below */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", marginLeft: 240 }}>
        {/* Header (top, profile on right) */}
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "fixed", top: 0, right: 0, left: 240, zIndex: 1000, justifyContent: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
            <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>Admission</span>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={profilePicUrl}
                alt="Profile"
                style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
              />
            </button>
            
            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 8,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 20,
                minWidth: 200,
                overflow: "hidden"
              }}>
                <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                  Admission Counselor
                </div>
                <button
                  onClick={() => { setDropdownOpen(false); setShowResetModal(true); }}
                  style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#155c2b", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }}
                  onMouseEnter={(e) => e.target.style.background = "#f5fff0"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Change Password
                </button>
                {/* Only show Log out option in the dropdown - Profile and Settings removed per design */}
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    handleLogout();
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#e67e22",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Log out
                </button>
                {showResetModal && <ResetPassword email={counselorEmail} onClose={() => setShowResetModal(false)} />}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: 40, minHeight: "100vh", paddingTop: 120 }}>
          {active === "home" && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 6px 18px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 800, color: '#155c2b', fontSize: 18 }}>Admission Analytics</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={selectedSchoolYear} onChange={e => setSelectedSchoolYear(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
                  <option value="all">All School Years</option>
                  {uniqueSchoolYears.map(sy => <option key={sy.key} value={sy.label}>{sy.label}</option>)}
                </select>
                <select value={selectedSemester} onChange={e => setSelectedSemester(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
                  <option value="all">All Semesters</option>
                  <option value="first">1st Semester</option>
                  <option value="second">2nd Semester</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div style={{ padding: 12, borderRadius: 8, background: '#f8fafb' }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Current Applicants (Review)</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#155c2b' }}>{metrics.applicantsReview}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Applicants awaiting review</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: '#f8fafb' }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Approved Applicants</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#155c2b' }}>{metrics.applicantsApproved}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Applicants already approved</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: '#f8fafb' }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Total Applicants</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#155c2b' }}>{metrics.totalApplicants}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Total (review + approved)</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: '#f8fafb' }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Officially Enrolled</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#155c2b' }}>{metrics.enrolledCount}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Officially Enrolled Students</div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1.4fr 0.8fr', gap: 12 }}>
              <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Applicant Trend</div>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  <LineSVG width={700} height={280} data={trendData} />
                </div>
              </div>

              <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Application Status Share</div>
                <div>
                  <PieSVG width={380} height={280} data={statusData} colors={['#e67e22', '#1e8f4a', '#155c2b']} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>By Category</div>
                {Object.keys(metrics.categoryCounts).length === 0 && <div style={{ color: '#888' }}>No data</div>}
                {Object.entries(metrics.categoryCounts).slice(0,8).map(([cat, cnt]) => {
                  const pct = metrics.totalApplicants ? Math.round((cnt / metrics.totalApplicants) * 100) : 0;
                  return (
                    <div key={cat} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><div style={{ fontSize: 13 }}>{cat}</div><div style={{ fontSize: 13, fontWeight: 700 }}>{cnt}</div></div>
                      <div style={{ height: 10, background: '#eee', borderRadius: 6 }}><div style={{ width: `${pct}%`, height: '100%', background: '#155c2b', borderRadius: 6 }} /></div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Programs (Top)</div>
                {Object.keys(metrics.programCounts).length === 0 && <div style={{ color: '#888' }}>No data</div>}
                {Object.entries(metrics.programCounts).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([prog, cnt]) => {
                  const pct = metrics.totalApplicants ? Math.round((cnt / metrics.totalApplicants) * 100) : 0;
                  return (
                    <div key={prog} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><div style={{ fontSize: 13 }}>{prog}</div><div style={{ fontSize: 13, fontWeight: 700 }}>{cnt}</div></div>
                      <div style={{ height: 10, background: '#eee', borderRadius: 6 }}><div style={{ width: `${pct}%`, height: '100%', background: '#155c2b', borderRadius: 6 }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1, padding: 12, borderRadius: 8, background: '#fff', border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Entrance Exam Results</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><div>Approved</div><div style={{ fontWeight: 700 }}>{metrics.passCount}</div></div>
                    <div style={{ height: 12, background: '#eee', borderRadius: 6, marginTop: 6 }}><div style={{ width: `${metrics.passCount + metrics.failCount ? Math.round((metrics.passCount/(metrics.passCount+metrics.failCount))*100) : 0}%`, height: '100%', background: '#1e8f4a', borderRadius: 6 }} /></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><div>Disapproved</div><div style={{ fontWeight: 700 }}>{metrics.failCount}</div></div>
                    <div style={{ height: 12, background: '#eee', borderRadius: 6, marginTop: 6 }}><div style={{ width: `${metrics.passCount + metrics.failCount ? Math.round((metrics.failCount/(metrics.passCount+metrics.failCount))*100) : 0}%`, height: '100%', background: '#c0392b', borderRadius: 6 }} /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
          {active === "assessment-approval" && <AssessmentApproval />}
          {active === "review-application" && <ReviewApplication />}
        </div>
      </div>
    </div>
  );
}
